import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { BudgetEntry } from './schema';

type Props = FieldExtensionComponentProps<
  BudgetEntry[] | undefined,
  Record<string, never>
>;

function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDefaultBudgets(now = new Date()): BudgetEntry[] {
  const firstStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const firstYear = firstStart.getFullYear();

  return Array.from({ length: 5 }, (_, index) => {
    const year = firstYear + index;
    const start = index === 0 ? firstStart : new Date(year, 0, 1);
    const end = new Date(year, 11, 31);

    return {
      name: `budget-${year}`,
      timeGrain: 'Annually',
      alertThresholds: [{ threshold: 90, operator: 'GreaterThan' }],
      timePeriod: {
        start: formatLocalDate(start),
        end: formatLocalDate(end),
      },
    };
  });
}

function normalizeBudgets(
  defaults: BudgetEntry[],
  current: BudgetEntry[] | undefined,
): BudgetEntry[] {
  return defaults.map((defaultBudget, index) => {
    const existing = current?.[index];
    return {
      name:
        typeof existing?.name === 'string' ? existing.name : defaultBudget.name,
      ...(typeof existing?.amount === 'number'
        ? { amount: existing.amount }
        : {}),
      timeGrain: 'Annually',
      alertThresholds: [{ threshold: 90, operator: 'GreaterThan' }],
      timePeriod: defaultBudget.timePeriod,
    };
  });
}

function budgetsMatch(
  current: BudgetEntry[] | undefined,
  expected: BudgetEntry[],
): boolean {
  return (
    current?.length === expected.length &&
    expected.every((budget, index) => {
      const existing = current[index];
      return (
        existing?.name === budget.name &&
        existing?.amount === budget.amount &&
        existing?.timeGrain === budget.timeGrain &&
        JSON.stringify(existing?.alertThresholds) ===
          JSON.stringify(budget.alertThresholds) &&
        existing?.timePeriod?.start === budget.timePeriod.start &&
        existing?.timePeriod?.end === budget.timePeriod.end
      );
    })
  );
}

/** Renders five annual budget entries with generated, read-only date ranges. */
export const BudgetSeriesField = (props: Props) => {
  const { onChange, formData, rawErrors, required } = props;
  const [defaultBudgets] = useState(getDefaultBudgets);
  const onChangeRef = useRef(onChange);
  const budgets = useMemo(
    () => normalizeBudgets(defaultBudgets, formData),
    [defaultBudgets, formData],
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!budgetsMatch(formData, budgets)) {
      onChangeRef.current(budgets);
    }
  }, [budgets, formData]);

  const updateBudget = (index: number, changes: Partial<BudgetEntry>) => {
    onChangeRef.current(
      budgets.map((budget, budgetIndex) =>
        budgetIndex === index ? { ...budget, ...changes } : budget,
      ),
    );
  };

  return (
    <Box display="flex" flexDirection="column" gridGap={16}>
      {budgets.map((budget, index) => (
        <Grid container spacing={2} alignItems="flex-start" key={index}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Budget Name"
              value={budget.name}
              onChange={event =>
                updateBudget(index, { name: event.target.value })
              }
              required={required}
              fullWidth
              margin="dense"
              variant="outlined"
              error={Boolean(rawErrors?.length)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Budget Amount (EUR)"
              type="number"
              value={budget.amount ?? ''}
              onChange={event => {
                const value = event.target.value;
                updateBudget(index, {
                  amount: value === '' ? undefined : Number(value),
                });
              }}
              required={required}
              fullWidth
              margin="dense"
              variant="outlined"
              inputProps={{ step: 'any' }}
              error={Boolean(rawErrors?.length)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Start Date"
              type="date"
              value={budget.timePeriod.start}
              required={required}
              fullWidth
              margin="dense"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              helperText={
                index === 0
                  ? 'First day of next month'
                  : 'First day of the year'
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="End Date"
              type="date"
              value={budget.timePeriod.end}
              required={required}
              fullWidth
              margin="dense"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              helperText={
                rawErrors?.length ? rawErrors[0] : 'Last day of the year'
              }
              error={Boolean(rawErrors?.length)}
            />
          </Grid>
        </Grid>
      ))}
    </Box>
  );
};
